/**
 * Suite product card, suite pieces, and the suite cart.
 *
 * Core product
 *   The option selects are the source of truth — the same ones the theme's
 *   ProductForm listens to. Stepping writes to the select and fires `change`, so
 *   the theme still owns variant id, availability, media and the URL. Everything
 *   written afterwards is a reconcile pass: it only touches the DOM when a value
 *   is actually out of date, so it never fights the theme.
 *
 * Suite pieces
 *   Deliberately isolated from the theme: their controls carry `data-suite-*`
 *   hooks only, and their hidden selects have no `name`, so the theme's
 *   section-wide queries never pick them up as core product options. Their
 *   state, pricing and cart lines are handled entirely here.
 *
 * Quantity rule
 *   Changing the core quantity pushes that value to everything that offers a
 *   quantity option — suite pieces, alternatives and add-ons alike. An item can
 *   then be adjusted on its own; that stands until the core quantity changes
 *   again.
 */
(function () {
  'use strict';

  var SECTION = '.suite-product';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function parseJSON(element) {
    if (!element) return null;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  function indexOf(element, attribute) {
    var raw = String(element.getAttribute(attribute) || '').replace('option', '');

    return parseInt(raw, 10) || 0;
  }

  function write(element, html) {
    if (element && element.innerHTML.trim() !== html) element.innerHTML = html;
  }

  /** Shopify's money format, applied to a cents value. */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function delimit(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';

      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);

      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var match = format.match(placeholder);
    var value;

    switch (match && match[1]) {
      case 'amount_no_decimals':
        value = delimit(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = delimit(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = delimit(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = delimit(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = delimit(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = delimit(cents, 2, "'", '.');
        break;
      default:
        value = delimit(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  /** Matches a set of option values against a variant list. */
  function findVariant(variants, values) {
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];

      if (options.length !== values.length) continue;

      var matched = true;

      for (var j = 0; j < values.length; j++) {
        if (options[j] !== values[j]) {
          matched = false;
          break;
        }
      }

      if (matched) return variants[i];
    }

    return null;
  }

  /** The count inside a quantity option value: "Set of 100" reads as 100. */
  function quantityValue(text) {
    var digits = String(text).replace(/[^\d]/g, '');

    return digits === '' ? NaN : parseInt(digits, 10);
  }

  /** The "Quantity: 40 • Solid Color" line shown in the cart. */
  function summarise(selects) {
    var quantity = '';
    var parts = [];

    selects.forEach(function (select) {
      var mode = select.getAttribute('data-suite-summary');

      if (mode === 'quantity') {
        quantity = select.getAttribute('data-suite-option-name') + ': ' + select.value;
      } else if (mode === 'value') {
        parts.push(select.value);
      }
    });

    if (quantity) parts.unshift(quantity);

    return parts.join(' • ');
  }

  /* ------------------ shipment */

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** Production weeks counted forward in business days, skipping weekends. */
  function shipByDate(weeks) {
    var days = Math.floor(weeks * 5);
    var date = new Date();
    var added = 0;

    while (added < days) {
      date.setDate(date.getDate() + 1);

      var day = date.getDay();

      if (day !== 0 && day !== 6) added++;
    }

    return MONTHS[date.getMonth()] + ' ' + date.getDate();
  }

  function setupShipment(scope) {
    toArray(scope.querySelectorAll('[data-suite-shipment]')).forEach(function (panel) {
      var target = panel.querySelector('[data-suite-shipment-date]');
      var weeks = parseFloat(panel.getAttribute('data-suite-shipment-weeks'));

      if (!target || isNaN(weeks)) return;

      target.textContent = shipByDate(weeks);
    });
  }

  /* --------------------------------------- estimate tip and its explainer */

  /** Shared, so opening the explainer also puts the tip away behind it. */
  function closeTip(section) {
    var tip = section.querySelector('[data-suite-tip]');

    if (!tip) return;

    var toggle = tip.querySelector('[data-suite-tip-toggle]');

    tip.setAttribute('data-suite-tip-open', 'false');

    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * The tip beside the estimated total opens on hover and on keyboard focus —
   * both handled in CSS. Touch has neither, so the same button toggles it
   * here, and a tap anywhere else puts it away.
   */
  function setupEstimateTip(section) {
    var tip = section.querySelector('[data-suite-tip]');

    if (!tip) return;

    var toggle = tip.querySelector('[data-suite-tip-toggle]');

    if (toggle) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();

        var open = tip.getAttribute('data-suite-tip-open') === 'true';

        tip.setAttribute('data-suite-tip-open', open ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    }

    // Runs after the toggle's own handler, and the toggle is inside the tip,
    // so opening never immediately closes itself.
    document.addEventListener('click', function (event) {
      if (!tip.contains(event.target)) closeTip(section);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeTip(section);
    });
  }

  /**
   * The full-screen explainer behind the tip's button. Focus moves into the
   * dialog on open, Tab is kept inside it while it is the only thing on
   * screen, and Escape or the scrim closes it and hands focus back to
   * whatever opened it.
   */
  function setupEstimateModal(section) {
    var modal = section.querySelector('[data-suite-modal]');

    if (!modal) return;

    var dialog = modal.querySelector('[data-suite-modal-dialog]');
    var lastFocused = null;

    function focusable() {
      if (!dialog) return [];

      return toArray(
        dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
    }

    function isOpen() {
      return modal.getAttribute('data-suite-modal-state') === 'true';
    }

    function open() {
      lastFocused = document.activeElement;

      modal.setAttribute('data-suite-modal-state', 'true');
      document.body.classList.add('suite-modal-locked');
      closeTip(section);

      var first = focusable()[0];

      if (first) first.focus();
    }

    function close() {
      if (!isOpen()) return;

      modal.setAttribute('data-suite-modal-state', 'false');
      document.body.classList.remove('suite-modal-locked');

      if (lastFocused && lastFocused.focus) lastFocused.focus();

      lastFocused = null;
    }

    toArray(section.querySelectorAll('[data-suite-modal-open]')).forEach(function (opener) {
      opener.addEventListener('click', function (event) {
        event.preventDefault();
        open();
      });
    });

    toArray(modal.querySelectorAll('[data-suite-modal-close]')).forEach(function (closer) {
      closer.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (!isOpen()) return;

      if (event.key === 'Escape') {
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = focusable();

      if (items.length === 0) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* -------------------------------------------------- mobile bottom sheet */

  var SHEET_QUERY = '(max-width: 959px)';

  /** Movement under this reads as a tap on the grip rather than a drag. */
  var TAP_SLOP = 8;

  /**
   * Below 960px the suite cart is a bottom sheet: collapsed it shows a strip
   * carrying the grip, the running total and add to cart; open it covers the
   * page.
   *
   * Collapsed, the sheet is pushed down by its own height less that strip, so
   * the strip's height is measured here and published as
   * --suite-cart-bar-height. Measuring beats a constant — the strip is worded
   * from theme settings, and longer wording or a larger base font makes it
   * taller.
   *
   * Above the breakpoint every part of this is display:none and the cart is
   * the ordinary sticky column it has always been.
   */
  function setupSheet(section) {
    var cart = section.querySelector('[data-suite-cart]');

    if (!cart) return;

    var strip = cart.querySelector('[data-suite-cart-top]');
    var grip = cart.querySelector('[data-suite-cart-handle]');
    var handles = toArray(cart.querySelectorAll('[data-suite-cart-handle]'));
    var closer = cart.querySelector('[data-suite-cart-close]');
    var barSubmit = cart.querySelector('[data-suite-cart-submit]');
    var scrim = section.querySelector('[data-suite-cart-scrim]');

    function isMobile() {
      return window.matchMedia(SHEET_QUERY).matches;
    }

    function isExpanded() {
      return cart.getAttribute('data-suite-cart-expanded') === 'true';
    }

    /**
     * Add-to-cart wording, which is set per breakpoint. The panel's button is
     * the theme's own, and updateBuyButton() rewrites its text from
     * data-lang-available on every variant change — so the swap is made there
     * rather than on the text node, which would be overwritten by the first
     * option click.
     *
     * Only the available wording is ours. Sold out and unavailable stay with
     * the theme.
     */
    var addButton = section.querySelector('[data-add-to-cart]');
    var mobileAddLabel = cart.getAttribute('data-suite-mobile-add-label');
    var desktopAddLabel = addButton && addButton.getAttribute('data-lang-available');

    function applyAddLabel() {
      if (!addButton || !mobileAddLabel || !desktopAddLabel) return;

      var wanted = isMobile() ? mobileAddLabel : desktopAddLabel;

      if (addButton.getAttribute('data-lang-available') === wanted) return;

      addButton.setAttribute('data-lang-available', wanted);

      if (addButton.hasAttribute('disabled')) return;

      var text = addButton.querySelector('[data-add-to-cart-text]');

      if (text) text.textContent = wanted;

      addButton.setAttribute('aria-label', wanted);
    }

    /** Only meaningful while collapsed: open, the strip drops its summary. */
    function measure() {
      if (!strip || isExpanded()) return;

      section.style.setProperty('--suite-cart-bar-height', strip.offsetHeight + 'px');
    }

    function setExpanded(expanded) {
      cart.setAttribute('data-suite-cart-expanded', expanded ? 'true' : 'false');

      handles.forEach(function (handle) {
        handle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });

      // Scoped to this breakpoint in CSS, so a resize can never strand it.
      document.body.classList.toggle('suite-cart-locked', expanded);

      // After the attribute lands, so the strip is back to its collapsed size.
      if (!expanded) window.setTimeout(measure, 0);
    }

    handles.forEach(function (handle) {
      handle.addEventListener('click', function (event) {
        event.preventDefault();

        // A drag has already decided where the sheet lands; the click that
        // follows the release would undo it.
        if (handle.dataset.suiteDragged === 'true') {
          delete handle.dataset.suiteDragged;
          return;
        }

        if (isMobile()) setExpanded(!isExpanded());
      });
    });

    /* Drag. The sheet follows the finger, then snaps to whichever end is
       nearer. Kept on the grip alone: the summary beside it is a tap target,
       and the add-to-cart button must not be draggable at all. */
    if (grip && window.PointerEvent) {
      var dragging = false;
      var startY = 0;
      var startOffset = 0;
      var offset = 0;
      var travel = 0;

      grip.addEventListener('pointerdown', function (event) {
        if (!isMobile()) return;

        travel = Math.max(cart.offsetHeight - (strip ? strip.offsetHeight : 0), 0);
        startY = event.clientY;
        startOffset = isExpanded() ? 0 : travel;
        offset = startOffset;
        dragging = true;

        cart.setAttribute('data-suite-cart-dragging', 'true');

        if (grip.setPointerCapture) grip.setPointerCapture(event.pointerId);
      });

      grip.addEventListener('pointermove', function (event) {
        if (!dragging) return;

        offset = Math.min(Math.max(startOffset + (event.clientY - startY), 0), travel);
        cart.style.transform = 'translateY(' + offset + 'px)';
      });

      function endDrag(event) {
        if (!dragging) return;

        dragging = false;

        cart.removeAttribute('data-suite-cart-dragging');
        cart.style.transform = '';

        // Too small to be a drag: leave it to the click handler, so the grip
        // behaves the same under a finger as it does under the keyboard.
        if (Math.abs(event.clientY - startY) < TAP_SLOP) return;

        grip.dataset.suiteDragged = 'true';
        setExpanded(offset < travel / 2);
      }

      grip.addEventListener('pointerup', endDrag);
      grip.addEventListener('pointercancel', endDrag);
    }

    if (closer) {
      closer.addEventListener('click', function (event) {
        event.preventDefault();
        setExpanded(false);
      });
    }

    if (scrim) {
      scrim.addEventListener('click', function () {
        setExpanded(false);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isExpanded()) setExpanded(false);
    });

    /**
     * Hands off to the real add-to-cart button inside the panel. The section
     * carries exactly one product form, so this cannot be a second submit.
     */
    if (barSubmit) {
      barSubmit.addEventListener('click', function (event) {
        event.preventDefault();

        var real = section.querySelector('[data-add-to-cart]');

        if (real) real.click();
      });
    }

    var resizeTimer = null;

    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);

      resizeTimer = window.setTimeout(function () {
        if (!isMobile() && isExpanded()) setExpanded(false);

        applyAddLabel();
        measure();
      }, 150);
    });

    applyAddLabel();
    measure();

    // The strip is set in a webfont, so its height is not final until that
    // font is. Both are cheap and idempotent.
    window.addEventListener('load', measure);

    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(measure);
    }
  }

  /* ------------------------------ pieces */

  function SuiteItem(section, root, onChange) {
    var data = parseJSON(root.querySelector('[data-suite-item-data]')) || {};
    var variants = data.variants || [];
    var handle = root.getAttribute('data-suite-item-handle');
    var line = section.querySelector('[data-suite-cart-line][data-suite-line-handle="' + handle + '"]');

    var selects = toArray(root.querySelectorAll('[data-suite-select]')).sort(function (a, b) {
      return indexOf(a, 'data-suite-index') - indexOf(b, 'data-suite-index');
    });

    var quantitySelect = root.querySelector('[data-suite-select][data-suite-summary="quantity"]');
    var priceElement = root.querySelector('[data-suite-item-price]');
    var counterValue = root.querySelector('[data-suite-item-counter-value]');
    var counterSteps = toArray(root.querySelectorAll('[data-suite-item-counter-step]'));

    var linePrice = line && line.querySelector('[data-suite-line-price]');
    var lineMeta = line && line.querySelector('[data-suite-line-meta]');
    var lineThumb = line && line.querySelector('[data-suite-line-thumb]');

    var choice = root.querySelector('[data-suite-item-choice]');

    var api = {
      cents: 0,
      variantId: null,
      // Selling plans the selected variant is allocated to, refreshed by
      // render(). Read at add-to-cart time to decide whether the shopper's
      // purchase option can be carried onto this line.
      sellingPlans: [],
      isActive: function () {
        return root.getAttribute('data-suite-item-active') === 'true';
      },
      root: root,
      choice: choice,
      isConditional: function () {
        return root.getAttribute('data-suite-item-conditional') === 'true';
      },

      /**
       * Shows or hides the card. The crossfade itself is CSS — the two cards
       * share a grid cell — so this only has to flip one class and nothing
       * here needs to know the timing.
       */
      setHidden: function (hidden) {
        var wrap = root.closest('[data-suite-swap]') || root;

        wrap.classList.toggle('suite-item-swap-out', hidden);
      },

      /** Sets membership without announcing it — the group reports once. */
      setChosen: function (chosen) {
        root.setAttribute('data-suite-item-active', chosen ? 'true' : 'false');
        render();
      }
    };

    function currentVariant() {
      return findVariant(
        variants,
        selects.map(function (select) {
          return select.value;
        })
      );
    }

    function refreshSteps() {
      if (!quantitySelect) return;

      counterSteps.forEach(function (step) {
        var target = quantitySelect.selectedIndex + parseInt(step.getAttribute('data-suite-item-counter-step'), 10);
        var outOfRange = target < 0 || target > quantitySelect.options.length - 1;

        step.setAttribute('aria-disabled', outOfRange ? 'true' : 'false');
      });
    }

    function render() {
      var variant = currentVariant();
      var active = api.isActive();

      api.cents = variant && active ? variant.cents : 0;
      api.variantId = variant ? variant.id : null;
      api.sellingPlans = (variant && variant.selling_plans) || [];

      if (variant) {
        write(priceElement, variant.price);
        write(linePrice, variant.price);

        if (lineThumb && variant.thumb && lineThumb.getAttribute('src') !== variant.thumb) {
          lineThumb.setAttribute('src', variant.thumb);
        }
      }

      if (quantitySelect && counterValue && counterValue.textContent.trim() !== quantitySelect.value) {
        counterValue.textContent = quantitySelect.value;
      }

      if (lineMeta) {
        var summary = summarise(selects);

        if (lineMeta.textContent.trim() !== summary) lineMeta.textContent = summary;
      }

      // Tab states
      selects.forEach(function (select) {
        var group = root.querySelector(
          '[data-suite-buttons][data-suite-index="' + select.getAttribute('data-suite-index') + '"]'
        );

        if (!group) return;

        toArray(group.querySelectorAll('[data-suite-button]')).forEach(function (button) {
          button.classList.toggle('selected', button.getAttribute('data-suite-value') === select.value);
        });
      });

      // The choice checkbox is not synced here: it reflects the group's pick,
      // not this card's own membership, so the group sets it.
      if (line) line.classList.toggle('suite-cart-item-inactive', !active);

      refreshSteps();
    }

    function changed() {
      render();
      onChange();
    }

    function step(offset) {
      if (!quantitySelect) return;

      var target = quantitySelect.selectedIndex + offset;

      if (target < 0 || target > quantitySelect.options.length - 1) return;

      quantitySelect.selectedIndex = target;
      changed();
    }

    counterSteps.forEach(function (control) {
      control.addEventListener('click', function (event) {
        event.preventDefault();
        step(parseInt(control.getAttribute('data-suite-item-counter-step'), 10));
      });
    });

    toArray(root.querySelectorAll('[data-suite-buttons]')).forEach(function (group) {
      var select = root.querySelector(
        '[data-suite-select][data-suite-index="' + group.getAttribute('data-suite-index') + '"]'
      );

      if (!select) return;

      toArray(group.querySelectorAll('[data-suite-button]')).forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          select.value = button.getAttribute('data-suite-value');
          changed();
        });
      });
    });

    function setActive(active) {
      root.setAttribute('data-suite-item-active', active ? 'true' : 'false');
      changed();
    }

    // Conditional cards are driven by their group instead, since membership
    // there is a single pick shared across the alternatives.
    var toggle = root.querySelector('[data-suite-item-toggle]');

    if (toggle && !api.isConditional()) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        setActive(!api.isActive());
      });
    }

    // Conditional cards hand their remove to the group too, so that removing
    // from the cart and adding back from the card stay in step.
    api.removeControl = line && line.querySelector('[data-suite-line-remove]');

    if (api.removeControl && !api.isConditional()) {
      api.removeControl.addEventListener('click', function (event) {
        event.preventDefault();
        setActive(false);
      });
    }

    /* Media: one visible slide, thumbnails and arrows step through it. */
    var slides = toArray(root.querySelectorAll('[data-suite-item-slide]'));
    var thumbs = toArray(root.querySelectorAll('[data-suite-item-thumb]'));
    var slideIndex = 0;

    function showSlide(next) {
      if (slides.length === 0) return;

      slideIndex = (next + slides.length) % slides.length;

      slides.forEach(function (slide, i) {
        slide.classList.toggle('suite-item-slide-hidden', i !== slideIndex);
      });

      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('selected', i === slideIndex);
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function (event) {
        event.preventDefault();
        showSlide(i);
      });
    });

    toArray(root.querySelectorAll('[data-suite-item-step]')).forEach(function (arrow) {
      arrow.addEventListener('click', function (event) {
        event.preventDefault();
        showSlide(slideIndex + parseInt(arrow.getAttribute('data-suite-item-step'), 10));
      });
    });

    /**
     * Core quantity pushed down, to suite pieces and add-ons alike. Silent when
     * this item has no quantity option at all.
     *
     * An exact value wins. Failing that the nearest count is taken, so an item
     * whose ladder does not line up with the core's — 25/50/100 against
     * 40/80/120 — still tracks it instead of sitting at its default price.
     */
    api.setQuantity = function (value) {
      if (!quantitySelect) return;

      var wanted = quantityValue(value);
      var target = -1;
      var closest = Infinity;

      for (var i = 0; i < quantitySelect.options.length; i++) {
        var option = quantitySelect.options[i];

        if (option.value === value) {
          target = i;
          break;
        }

        var distance = Math.abs(quantityValue(option.value) - wanted);

        if (!isNaN(distance) && distance < closest) {
          closest = distance;
          target = i;
        }
      }

      if (target < 0 || quantitySelect.selectedIndex === target) return;

      quantitySelect.selectedIndex = target;
      render();
    };

    api.render = render;
    render();

    return api;
  }

  /* ------------------------------------------------------------------ core */

  /**
   * Thumbnail strip under the core product gallery, matching the one on the
   * suite piece cards.
   *
   * The gallery is in the theme's "thumbnails" mode, so it shows one image at
   * a time exactly as a piece card does. Rather than reimplement that
   * switching, each thumbnail here proxies the theme's own thumbnail for the
   * same media id — the theme keeps ownership of variant media grouping,
   * video, and 3D models, and this strip is only the visible control. The
   * theme's vertical rail is hidden in CSS on these templates.
   */
  function setupCoreThumbs(section) {
    var strip = section.querySelector('[data-suite-core-thumbs]');

    if (!strip) return;

    function select(id) {
      toArray(section.querySelectorAll('[data-suite-core-thumb]')).forEach(function (thumb) {
        thumb.classList.toggle('selected', thumb.getAttribute('data-suite-core-thumb') === id);
      });
    }

    // Delegated rather than bound per button: the strip sits inside
    // .product__primary-left, and the theme replaces that column's innerHTML
    // wholesale when a variant brings in different media, which would take
    // listeners bound to the buttons themselves down with it.
    section.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-suite-core-thumb]');

      if (!thumb) return;

      event.preventDefault();

      var id = thumb.getAttribute('data-suite-core-thumb');
      var proxy = section.querySelector('[data-product-thumbnail][data-thumbnail-id="' + id + '"]');

      // Variant media grouping can drop a thumbnail from the theme's own
      // rail. With nothing to proxy there is nothing to switch to, and
      // selecting it anyway would misreport what is on screen.
      if (!proxy) return;

      select(id);
      proxy.click();
    });

    // The theme also switches media on its own — a variant change moves the
    // featured image without anything here being clicked — so selection
    // follows whichever item is actually on screen rather than the last click.
    if (!window.IntersectionObserver) return;

    var observer = new window.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) select(entry.target.getAttribute('data-media-item-id'));
        });
      },
      { threshold: 0.6 }
    );

    toArray(section.querySelectorAll('.product__media-item[data-media-item-id]')).forEach(function (item) {
      observer.observe(item);
    });
  }

  function setup(section) {
    if (section.dataset.suiteProductReady === 'true') return;
    section.dataset.suiteProductReady = 'true';

    // Ahead of the option wiring below, which bails on products with no options.
    setupShipment(section);
    setupSheet(section);
    setupCoreThumbs(section);
    setupEstimateTip(section);
    setupEstimateModal(section);

    var variants = parseJSON(section.querySelector('[data-variant-json]')) || [];
    var prices = parseJSON(section.querySelector('[data-suite-prices]')) || {};
    var variantSelect = section.querySelector('[data-variant-select]');
    var priceElements = toArray(section.querySelectorAll('[data-product-price]'));
    var compareElements = toArray(section.querySelectorAll('[data-compare-price]'));
    var cartMeta = section.querySelector('[data-suite-cart-meta]');
    var cartThumb = section.querySelector('[data-suite-cart-thumb]');
    var cartTotal = section.querySelector('[data-suite-cart-total]');
    var cartBarTotal = section.querySelector('[data-suite-cart-bar-total]');
    var cartDeposit = section.querySelector('[data-suite-cart-deposit]');
    var cartCount = section.querySelector('[data-suite-cart-count]');

    // Core controls only: suite pieces use `data-suite-*` hooks throughout.
    var selects = toArray(section.querySelectorAll('select[data-index]')).sort(function (a, b) {
      return indexOf(a, 'data-index') - indexOf(b, 'data-index');
    });

    if (selects.length === 0) return;

    var coreQuantity = section.querySelector('select[data-index][data-suite-summary="quantity"]');

    // Left unset so the first reconcile pushes the core quantity down, putting
    // every piece in step on load rather than only after the first change.
    var lastQuantity = null;
    var coreCents = 0;
    var items = [];

    /**
     * The cart total, and the same figures on the mobile sheet's collapsed
     * bar. The core product is always counted — it cannot be removed.
     */
    function updateTotal() {
      var total = coreCents;
      var count = 1;

      items.forEach(function (item) {
        total += item.cents;

        if (item.isActive()) count += 1;
      });

      var money = formatMoney(total);

      write(cartTotal, money);
      write(cartBarTotal, money);

      // The share due at checkout. Absent unless the deposit row is switched
      // on for this template, so the element is what decides whether there is
      // anything to work out at all.
      if (cartDeposit) {
        var percent = parseFloat(cartDeposit.getAttribute('data-suite-deposit-percent'));

        if (!isNaN(percent)) write(cartDeposit, formatMoney(Math.round((total * percent) / 100)));
      }

      if (cartCount) {
        var word = count === 1 ? 'data-suite-count-one' : 'data-suite-count-many';

        write(cartCount, count + ' ' + (cartCount.getAttribute(word) || ''));
      }
    }

    /**
     * The alternatives behave as one piece with two faces, described by two
     * separate facts:
     *
     *   conditionalIndex   which alternative is on screen, flipped by the
     *                      checkbox
     *   conditionalActive  whether that alternative is in the suite, cleared by
     *                      removing its cart line and set again by its button
     *
     * Keeping these apart matters: folding them into a single "chosen index"
     * made a removed alternative impossible to add back, because the group
     * still believed that index was the chosen one and ignored the button.
     */
    var conditionalIndex = 0;
    var conditionalActive = true;

    var conditionalsRoot = section.querySelector('[data-suite-conditional-group]');
    var swapTimer = null;

    function conditionalGroup() {
      return items.filter(function (item) {
        return item.isConditional();
      });
    }

    /** Read from the stylesheet, so the timing has one definition. */
    function swapDuration() {
      if (!conditionalsRoot) return 320;

      var raw = window.getComputedStyle(conditionalsRoot).getPropertyValue('--suite-swap-duration').trim();
      var value = parseFloat(raw);

      if (isNaN(value)) return 320;

      return raw.indexOf('ms') > -1 ? value : value * 1000;
    }

    /** Covers the pair for the length of the crossfade. */
    function markSwapping() {
      if (!conditionalsRoot) return;

      conditionalsRoot.setAttribute('data-suite-swapping', 'true');
      window.clearTimeout(swapTimer);

      swapTimer = window.setTimeout(function () {
        conditionalsRoot.setAttribute('data-suite-swapping', 'false');
      }, swapDuration());
    }

    function applyConditionals() {
      var group = conditionalGroup();

      if (group.length === 0) return;
      if (conditionalIndex > group.length - 1) conditionalIndex = 0;

      group.forEach(function (item, index) {
        var visible = index === conditionalIndex;

        item.setChosen(visible && conditionalActive);
        item.setHidden(!visible);

        if (item.choice) item.choice.checked = conditionalIndex > 0;
      });
    }

    function updateConditionals(index, active) {
      // Only a genuine swap gets the overlay. Adding or removing the piece
      // already on screen is an instant change with nothing to cover.
      if (index !== conditionalIndex) markSwapping();

      conditionalIndex = index;
      conditionalActive = active;
      applyConditionals();
      updateTotal();
    }

    function selectedVariant() {
      return findVariant(
        variants,
        selects.map(function (select) {
          return select.value;
        })
      );
    }

    function syncLabels() {
      selects.forEach(function (select) {
        var label = section.querySelector(
          '[data-selected-value-for-option][data-option-name="' + select.getAttribute('data-index') + '"]'
        );

        if (label && label.textContent.trim() !== select.value) {
          label.textContent = select.value;
        }
      });
    }

    function syncChoices() {
      selects.forEach(function (select) {
        var group = select.parentNode;
        var chosen = select.options[select.selectedIndex];

        if (!group || !chosen) return;

        toArray(group.querySelectorAll('[data-button]')).forEach(function (button) {
          var isChosen = button.getAttribute('data-option-handle') === chosen.getAttribute('data-value-handle');

          button.classList.toggle('selected', isChosen);
        });
      });
    }

    function syncPrice(variant) {
      var money = variant && prices[String(variant.id)];

      // Sold out and zero-price display follow theme settings, so those cases
      // are left to the theme's own price handling.
      if (!money || money.available !== true) return;

      coreCents = money.cents;

      priceElements.forEach(function (element) {
        write(element, money.price);
      });

      compareElements.forEach(function (element) {
        write(element, money.compare);
      });

      if (cartThumb && money.thumb && cartThumb.getAttribute('src') !== money.thumb) {
        cartThumb.setAttribute('src', money.thumb);
      }
    }

    function syncCartSummary() {
      if (!cartMeta) return;

      var summary = summarise(selects);

      if (cartMeta.textContent.trim() !== summary) cartMeta.textContent = summary;
    }

    function pushQuantity() {
      if (!coreQuantity || coreQuantity.value === lastQuantity) return;

      lastQuantity = coreQuantity.value;

      items.forEach(function (item) {
        item.setQuantity(lastQuantity);
      });
    }

    function reconcile() {
      var variant = selectedVariant();

      syncLabels();
      syncChoices();
      syncPrice(variant);
      syncCartSummary();
      pushQuantity();
      updateTotal();

      if (variant && variantSelect && variantSelect.value !== String(variant.id)) {
        variantSelect.value = String(variant.id);
      }
    }

    // Runs after every synchronous listener, whichever order they registered in.
    function reconcileLater() {
      window.setTimeout(reconcile, 0);
    }

    function choose(select, index) {
      if (index < 0 || index > select.options.length - 1) return;

      // No-ops when the theme's own button handler already applied the change.
      if (index !== select.selectedIndex) {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      reconcileLater();
    }

    function indexOfHandle(select, handle) {
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].getAttribute('data-value-handle') === handle) return i;
      }

      return -1;
    }

    selects.forEach(function (select) {
      select.addEventListener('change', reconcileLater);
    });

    // Tabs and swatches. The theme binds these too; this is the fallback that
    // keeps them working if its binding never ran, and stays inert if it did.
    toArray(section.querySelectorAll('[data-option-buttons]')).forEach(function (group) {
      var select = group.querySelector('select');

      if (!select) return;

      toArray(group.querySelectorAll('[data-button]')).forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          choose(select, indexOfHandle(select, button.getAttribute('data-option-handle')));
        });
      });
    });

    toArray(section.querySelectorAll('[data-suite-counter]')).forEach(function (counter) {
      var select = counter.querySelector('select');
      var steps = toArray(counter.querySelectorAll('[data-suite-counter-step]'));

      if (!select) return;

      function refreshSteps() {
        steps.forEach(function (step) {
          var target = select.selectedIndex + parseInt(step.getAttribute('data-suite-counter-step'), 10);
          var outOfRange = target < 0 || target > select.options.length - 1;

          step.setAttribute('aria-disabled', outOfRange ? 'true' : 'false');
        });
      }

      steps.forEach(function (step) {
        step.addEventListener('click', function (event) {
          event.preventDefault();
          choose(select, select.selectedIndex + parseInt(step.getAttribute('data-suite-counter-step'), 10));
          refreshSteps();
        });
      });

      select.addEventListener('change', refreshSteps);
      refreshSteps();
    });

    items = toArray(section.querySelectorAll('[data-suite-item]')).map(function (root) {
      return SuiteItem(section, root, updateTotal);
    });

    conditionalGroup().forEach(function (item, index) {
      // Swapping to an alternative is also a choice to have it, so a piece
      // removed earlier comes back when the shopper switches.
      if (item.choice) {
        item.choice.addEventListener('change', function () {
          updateConditionals(item.choice.checked ? 1 : 0, true);
        });
      }

      var toggle = item.root && item.root.querySelector('[data-suite-item-toggle]');

      if (toggle) {
        toggle.addEventListener('click', function (event) {
          event.preventDefault();
          updateConditionals(index, !(index === conditionalIndex && conditionalActive));
        });
      }

      if (item.removeControl) {
        item.removeControl.addEventListener('click', function (event) {
          event.preventDefault();
          updateConditionals(index, false);
        });
      }
    });

    // Matches the markup's starting state; identical classes mean no transition.
    applyConditionals();

    /* ------------------------------------------------------- add to cart */

    /**
     * The core product is still added by the theme's own form submit, so its
     * line item properties — personalisation fields among them — are
     * serialised exactly as they are on every other template. Only the active
     * suite pieces are added here first, then the submit is handed back.
     */
    var form = section.querySelector('[data-product-form]');
    var addButton = section.querySelector('[data-add-to-cart]');
    var quantityError = section.querySelector('[data-quantity-error]');
    var submitting = false;

    /**
     * One id per add-to-cart, carried by every line the suite creates. The
     * cart page groups on it, and it is what a Cart Transform function would
     * key on to merge the lines into a real bundle at checkout.
     */
    function bundleId() {
      return 'suite-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    }

    /**
     * Properties are prefixed with an underscore, which Shopify and this theme
     * both take as "do not show the shopper". They still reach the order, so
     * the bundle is legible in the admin.
     *
     * `items` follows the cards' DOM order — alternatives, suite pieces,
     * optional pieces, add-ons — which is also the suite cart's order.
     * _suite_order records each piece's place in it, because Shopify does not
     * keep a batch add's lines in the order they were sent; the cart page sorts
     * the bundle on it so both lists read the same.
     */
    function activeSuiteItems(bundle) {
      var plan = chosenSellingPlan();

      return items
        .filter(function (item) {
          return item.isActive() && item.variantId;
        })
        .map(function (item, index) {
          var line = {
            id: item.variantId,
            quantity: 1,
            properties: {
              _suite_bundle: bundle,
              _suite_role: 'child',
              _suite_order: String(index + 1)
            }
          };

          // Only when this variant is actually allocated to that plan.
          // Shopify rejects the whole request if a line names a plan its
          // variant has no allocation for, which would fail the entire add.
          if (plan && allowsPlan(item.sellingPlans, plan)) {
            line.selling_plan = plan;
          }

          return line;
        });
    }

    /** ES5 contains, tolerating the id arriving as a number or a string. */
    function allowsPlan(plans, plan) {
      if (!plans || !plans.length) return false;

      for (var i = 0; i < plans.length; i++) {
        if (String(plans[i]) === String(plan)) return true;
      }

      return false;
    }

    /**
     * The purchase option the shopper picked for the core product, so the
     * suite pieces and add-ons go in on the same terms. Downpay renders its
     * deposit / pay-in-full control as a `selling_plan` field, which sits in
     * the app block rather than inside the form element — the whole section is
     * searched for that reason. Pay in full carries no plan, and returning
     * null there is what keeps the extras one-time purchases to match.
     */
    function chosenSellingPlan() {
      var scope = section || document;
      var radios = scope.querySelectorAll('input[type="radio"][name="selling_plan"]');
      var field = radios.length
        ? scope.querySelector('input[type="radio"][name="selling_plan"]:checked')
        : scope.querySelector('[name="selling_plan"]');

      if (!field) return null;

      var value = String(field.value || '').trim();

      // Downpay writes an empty value, and some themes a literal 0, for the
      // pay-in-full choice.
      return value && value !== '0' ? value : null;
    }

    /** Adds or updates a hidden field on the core product's form. */
    function setFormValue(name, value) {
      var input = form.querySelector('input[name="' + name + '"]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }

      input.value = value;
    }

    function cartRoutes() {
      return (window.theme && window.theme.routes && window.theme.routes.cart) || {};
    }

    function addToCartUrl() {
      return cartRoutes().add || '/cart/add.js';
    }

    /**
     * Submits the core product natively rather than through the theme's AJAX
     * path. The browser posts every field in the form — personalisation
     * properties included — and Shopify answers with a redirect to the cart,
     * which is where this button is meant to land.
     */
    function submitToCart(bundle) {
      var variant = selectedVariant();

      // The theme normally syncs this on its own submit, which is skipped here.
      if (variant && variantSelect) variantSelect.value = String(variant.id);

      // The same id the pieces went in with, marking this line as their parent.
      setFormValue('properties[_suite_bundle]', bundle);
      setFormValue('properties[_suite_role]', 'parent');
      setFormValue('return_to', cartRoutes().base || '/cart');

      form.submit();
    }

    function showError(message) {
      if (!quantityError) return;

      quantityError.innerText = message;
      quantityError.classList.remove('hidden');
    }

    if (form && addButton) {
      addButton.addEventListener('click', function (event) {
        if (submitting) return;

        // Always handled here: this button adds the whole suite and lands on
        // the cart page, so the theme's drawer flow is bypassed either way.
        event.preventDefault();
        submitting = true;
        addButton.classList.add('loading');

        var bundle = bundleId();
        var extras = activeSuiteItems(bundle);

        // Suite pieces and add-ons go first over AJAX; the core product then
        // follows on the native submit, which carries the redirect.
        var pending = extras.length
          ? window
              .fetch(addToCartUrl(), {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: extras })
              })
              .then(function (response) {
                return response.json().then(function (data) {
                  if (!response.ok) throw new Error(data.description || data.message || 'Cart error');

                  return data;
                });
              })
          : window.Promise.resolve();

        pending
          .then(function () {
            // Navigation follows, so the loading state is left in place.
            submitToCart(bundle);
          })
          .catch(function (error) {
            submitting = false;
            addButton.classList.remove('loading');
            showError(error.message);

            // Keep the drawer and header count truthful about anything that
            // did land in the cart before the failure.
            document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          });
      });
    }

    reconcile();
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;

    if (scope.matches && scope.matches(SECTION)) setup(scope);

    toArray(scope.querySelectorAll(SECTION)).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // The theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
